import {getAccountBalanceAmount} from '../utils';

import type {TrustAccountListMode} from '../hooks';
import type {TrustPurpose} from '@sollapay/enums';
import type {FirmTrustAccountListItemDTO, TrustAccountListLedgerDTO} from '@sollapay/types';
import type {ListSortConfig, ListSortFieldConfig} from '@sollapay/ui/components';

interface SortableTrustAccount {
  trustPurpose?: TrustPurpose | null;
}

/** The fields every trust-account list sorts by; each list adds its own date and extras. */
export function trustAccountSortFields<T extends SortableTrustAccount>(fields: {
  getName: (account: T) => string;
  getBalance: (account: T) => number | null;
}): Record<'trustName' | 'type' | 'balance', ListSortFieldConfig<T>> {
  return {
    trustName: {i18nKey: 'sorting.options.trustName', defaultDesc: false, getValue: fields.getName},
    type: {
      i18nKey: 'sorting.options.type',
      defaultDesc: false,
      getValue: account => account.trustPurpose ?? ''
    },
    balance: {i18nKey: 'sorting.options.balance', defaultDesc: true, getValue: fields.getBalance}
  };
}

interface TrustAccountSortConfigParams {
  balancesMap?: Record<string, TrustAccountListLedgerDTO>;
  mode?: TrustAccountListMode;
}

export const TRUST_ACCOUNT_SORT_CONFIG = ({
  balancesMap,
  mode = 'own'
}: TrustAccountSortConfigParams): ListSortConfig<FirmTrustAccountListItemDTO> => ({
  order: [
    'createdAt',
    'trustName',
    'type',
    'balance',
    ...(mode === 'own' ? [] : ['responsibleTrustee']),
    'developer'
  ],
  fields: {
    createdAt: {
      i18nKey: 'sorting.options.createdAt',
      defaultDesc: true,
      getValue: (account: FirmTrustAccountListItemDTO) => new Date(account.createdAt).getTime()
    },
    ...trustAccountSortFields<FirmTrustAccountListItemDTO>({
      getName: account => account.name,
      getBalance: account => getAccountBalanceAmount(account.id, account.trustStatus, balancesMap)
    }),
    responsibleTrustee: {
      i18nKey: 'sorting.options.responsibleTrustee',
      defaultDesc: false,
      getValue: (account: FirmTrustAccountListItemDTO) => account.lawyerName ?? ''
    },
    developer: {
      i18nKey: 'sorting.options.seller',
      defaultDesc: false,
      getValue: (account: FirmTrustAccountListItemDTO) => account.sellerName ?? ''
    }
  }
});
